/** One step of a prefix trail: the segment's own name and the prefix that navigates to it. */
export interface PrefixSegment {
  label: string;
  prefix: string;
}

/**
 * Split an S3 prefix into the cumulative trail of folders leading to it.
 *
 * `"a/b/"` yields the steps for `"a/"` and `"a/b/"`. The bucket root is no step at all, so an
 * empty prefix yields nothing and the caller renders whatever it uses for the root.
 */
export function prefixSegments(prefix: string): PrefixSegment[] {
  const segments = prefix.split("/").filter((segment) => segment.length > 0);

  let cumulative = "";
  return segments.map((segment) => {
    cumulative += `${segment}/`;
    return { label: segment, prefix: cumulative };
  });
}

/**
 * Strip the enclosing prefix, and any trailing delimiter, off a key for display.
 *
 * Every row in a level shares that level's prefix, so repeating it in each cell would crowd out
 * the part that differs. The full key stays available for a tooltip and for the CSV.
 */
export function entryDisplayName(key: string, prefix: string): string {
  const withoutPrefix = key.startsWith(prefix) ? key.slice(prefix.length) : key;
  return withoutPrefix.endsWith("/")
    ? withoutPrefix.slice(0, -1)
    : withoutPrefix;
}

/**
 * Strip leading and trailing slashes and collapse repeats.
 *
 * A dataset holds one file per path, and the server compares the path it is given without
 * normalizing it, so `/logs/a` and `logs/a` would be two records for one object.
 */
export function normalizeRelativePath(path: string): string {
  return path
    .trim()
    .split("/")
    .filter((segment) => segment.length > 0)
    .join("/");
}
